"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Box,
  HStack,
  Button,
  Flex,
  Container,
  Tabs,
  useTabs,
  Icon,
  VStack,
} from "@chakra-ui/react";
import useSWR from "swr";
import { useLocale } from "next-intl";
import { OverviewTab } from "./OverviewTab";
import { SuggestionTab } from "./SuggestionTab";
import { ATSTab } from "./ATSTab";
import { SignupDialog } from "./SignupDialog";
import { ResultHeader } from "./ResultHeader";
import { FaChartLine, FaLightbulb } from "react-icons/fa6";
import { useBenchmarkContext } from "../_context/BenchmarkContext";
import { FaFileAlt } from "react-icons/fa";
import { mapBenchmarkDetailsToAnalysisResult } from "../utils/mapBenchmarkDetails";
import { useAuthStore } from "@/_stores/useAuthStore";
import { useRouter } from "next/navigation";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { PiSparkleFill } from "react-icons/pi";
import { resumeBenchmarkService } from "@/_services/resumeServices/benchmark";

interface ResultScreenProps {
  onReset: () => void;
}

const TABS = [
  { id: "overview", label: "Overview", icon: <Icon as={FaChartLine} /> },
  { id: "suggestion", label: "Suggestions", icon: <Icon as={FaLightbulb} /> },
  { id: "ats", label: "ATS Resume", icon: <Icon as={FaFileAlt} /> },
] as const;

export const ResultScreen = ({ onReset }: ResultScreenProps) => {
  const { benchmarkDetails, resumeId } = useBenchmarkContext();
  const [showSignup, setShowSignup] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | undefined>();
  const tabContext = useTabs({ defaultValue: "overview" });
  const token = useAuthStore((state) => state.token);
  const router = useRouter();
  const locale = useLocale();

  const { data: ranking } = useSWR(
    resumeId ? `/resume-benchmark/${resumeId}/ranking` : null,
    () => resumeBenchmarkService.getBenchmarkRanking(resumeId!),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const data = useMemo(
    () =>
      benchmarkDetails
        ? mapBenchmarkDetailsToAnalysisResult(benchmarkDetails)
        : null,
    [benchmarkDetails],
  );

  useEffect(() => {
    if (!resumeId) {
      setShareUrl(undefined);
      return;
    }
    setShareUrl(
      `${window.location.origin}/${locale}/resume-ranking/${resumeId}/share`,
    );
  }, [locale, resumeId]);

  const currentIndex = TABS.findIndex((t) => t.id === tabContext.value);

  const goNext = () => {
    if (currentIndex < TABS.length - 1)
      tabContext.setValue(TABS[currentIndex + 1].id);
  };
  const goPrev = () => {
    if (currentIndex > 0) tabContext.setValue(TABS[currentIndex - 1].id);
  };

  if (!data) return null;

  const saveResults = () => {
    if (currentIndex < TABS.length - 1) {
      goNext();
    } else {
      if (token) {
        router.push("/resume-benchmark/home");
      } else {
        setShowSignup(true);
      }
    }
  };

  const rankingIndustry =
    ranking?.industry ?? benchmarkDetails?.industry ?? null;
  const rankingPercentile =
    ranking?.percentile ?? benchmarkDetails?.percentile ?? null;

  return (
    <>
      <Tabs.RootProvider value={tabContext} variant="plain">
        <Box
          minH="100vh"
          display="flex"
          flexDirection="column"
          id="result-screen"
        >
          <ResultHeader
            candidateName={data.candidateName}
            candidateTitle={data.candidateTitle}
            tabs={TABS}
            currentIndex={currentIndex}
            onReset={onReset}
          />

          <Box flex={1} py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }}>
            <Container maxW="900px">
              <Tabs.Content value="overview" p={0}>
                <VStack gap={6} align="stretch">
                  <OverviewTab
                    data={data}
                    rankingPercentile={rankingPercentile}
                    rankingIndustry={rankingIndustry}
                    shareUrl={shareUrl}
                  />
                </VStack>
              </Tabs.Content>
              <Tabs.Content value="suggestion" p={0}>
                <SuggestionTab
                  data={data}
                  onSignupRequest={() => setShowSignup(true)}
                />
              </Tabs.Content>
              <Tabs.Content value="ats" p={0}>
                <ATSTab
                  data={data}
                  onSignupRequest={() => setShowSignup(true)}
                />
              </Tabs.Content>
            </Container>
          </Box>

          <Box
            position="sticky"
            bottom={0}
            bg="rgba(9,9,18,0.9)"
            backdropFilter="blur(20px)"
            borderTop="1px solid"
            borderColor="whiteAlpha.100"
            py={4}
            px={{ base: 4, md: 8 }}
          >
            <Container maxW="900px">
              <Flex justify="space-between" align="center">
                <Button
                  variant="ghost"
                  rounded="xl"
                  size="sm"
                  color="neutral.500"
                  fontSize="sm"
                  _hover={{ color: "white", bg: "whiteAlpha.100" }}
                  onClick={goPrev}
                  disabled={currentIndex === 0}
                  gap={2}
                >
                  <Icon as={HiArrowLeft} color="inherit" />{" "}
                  {currentIndex > 0 ? TABS[currentIndex - 1].label : ""}
                </Button>

                <HStack gap={2}>
                  {TABS.map((t, i) => (
                    <Box
                      key={t.id}
                      as="button"
                      w={i === currentIndex ? 6 : 2}
                      h={2}
                      rounded="full"
                      bg={i === currentIndex ? "indigo.400" : "whiteAlpha.200"}
                      transition="all 0.3s"
                      onClick={() => tabContext.setValue(t.id)}
                    />
                  ))}
                </HStack>

                <Button
                  rounded="xl"
                  size="sm"
                  bg={
                    currentIndex < TABS.length - 1 ? "primary.600" : "green.600"
                  }
                  color="neutral.50"
                  fontWeight="bold"
                  fontSize="sm"
                  _hover={{
                    transform: "translateY(-1px)",
                    bg:
                      currentIndex < TABS.length - 1
                        ? "whiteAlpha.100"
                        : "green.500",
                  }}
                  transition="all 0.2s"
                  onClick={saveResults}
                  gap={2}
                >
                  {currentIndex < TABS.length - 1 ? (
                    <>
                      {TABS[currentIndex + 1].label}
                      <Icon as={HiArrowRight} color="inherit" />
                    </>
                  ) : (
                    <>
                      Save Results
                      <Icon as={PiSparkleFill} color="inherit" />
                    </>
                  )}
                </Button>
              </Flex>
            </Container>
          </Box>
        </Box>
      </Tabs.RootProvider>

      <SignupDialog isOpen={showSignup} onClose={() => setShowSignup(false)} />
    </>
  );
};
